import { getNavServiceItems } from "@/lib/service-nav-items";
import { getSiteUiCopy } from "@/lib/site-ui-copy";
import { t } from "@/lib/content";
import { getSiteNavResourceItems } from "@/lib/cc-nav-resources";
import {
  buildBlogHref,
  buildLocalizedPath,
  buildServiceHref,
  localeFromPathname,
  parseLocaleFromPathname,
  resolveMarketingRoute,
} from "@/lib/i18n/paths";
import { pickBlogLocalized, type BlogPostRecord } from "@/lib/blog-cards";
import { toSiteLocale } from "@/lib/i18n/config";
import { getHubspotBreadcrumbCopy } from "@/lib/i18n/routing-catalog";
import { getNavServiceMegaColumn } from "@/lib/cc-nav-services-mega";
import { getProjectRouteBySlug as getProjectBySlug } from "@/lib/i18n/routing-catalog";
import { getServiceRouteByTag as getServiceByTag } from "@/lib/i18n/routing-catalog";
import type { Lang } from "@/lib/types";
import type { BlogLocaleNavigation } from "@/lib/blog/locale-navigation";
import categories from "@/data/blog/lib/categories.json";
import { getBlogCategoryLabel } from "@/lib/blog/category-label";

export type NavBreadcrumbTrail = {
  parent: { label: string; href: string };
  /** Middle segment (e.g. Blog index on a post detail route). */
  middle?: { label: string; href: string };
  current: string;
};

export type NavBreadcrumbDeps = {
  getBlogPostBySlug?: (slug: string) => Pick<BlogPostRecord, "title"> | null;
  blogNavigation?: BlogLocaleNavigation | null;
};

function formatBlogPostSlugAsTitle(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function blogPostNavCurrentLabel(
  postSlug: string,
  lang: Lang,
  deps?: NavBreadcrumbDeps,
): string {
  const post = deps?.getBlogPostBySlug?.(postSlug) ?? null;
  return (
    (post ? pickBlogLocalized(post.title, lang) : "") || formatBlogPostSlugAsTitle(postSlug)
  );
}

export function navBreadcrumbRememberedDetail(trail: NavBreadcrumbTrail): string {
  if (trail.middle) {
    return `${trail.middle.label} / ${trail.current}`;
  }
  return trail.current;
}

/** Prefer server `initialNavBreadcrumb` on the first paint for `initialPathname`. */
export function resolveNavbarBreadcrumbForPathname(
  pathname: string,
  initialPathname: string,
  initialNavBreadcrumb: NavBreadcrumbTrail | null,
  clientNavBreadcrumb: NavBreadcrumbTrail | null,
): NavBreadcrumbTrail | null {
  if (pathname === initialPathname) {
    return initialNavBreadcrumb;
  }
  return clientNavBreadcrumb;
}

/** Whether held/remembered inline nav detail may show when live route detail is absent. */
export function shouldHoldNavDetailFallback(
  href: string,
  pathname: string,
  initialPathname: string,
  liveDetail: string | null,
  heldDetail: string | null,
  detailRailActive: boolean,
  isNavSectionActive: (sectionHref: string) => boolean,
): boolean {
  if (detailRailActive) {
    return Boolean(heldDetail);
  }
  if (pathname === initialPathname) {
    return false;
  }
  if (!heldDetail) {
    return false;
  }
  if (liveDetail) {
    return true;
  }
  if (pathname === href || pathname === `${href}/`) {
    return false;
  }
  return isNavSectionActive(href);
}

function findNavSectionLink(
  links: { label: string; href: string }[],
  locale: ReturnType<typeof toSiteLocale>,
  customPath: "services" | "projects",
): { label: string; href: string } {
  const href = buildLocalizedPath({ locale, customPath });
  const match = links.find((link) => link.href === href);
  return match ?? {
    label: getSiteUiCopy(locale).navigation[customPath],
    href,
  };
}

function blogNavSectionLink(
  lang: Lang,
  links: { label: string; href: string }[],
): { label: string; href: string } {
  const blogHref = buildBlogHref(lang);
  const blogLink = links.find((link) => link.href === blogHref);
  return blogLink ?? {
    label: blogNavDetailLabel(lang),
    href: blogHref,
  };
}

function blogNavDetailLabel(lang: Lang): string {
  const blogItem = getSiteNavResourceItems(lang).find((item) => item.key === "blog");
  if (blogItem) {
    return blogItem.label;
  }
  return getSiteUiCopy(lang).navigation.blog;
}

function serviceSubpageNavCurrentLabel(
  serviceTag: string,
  subKey: string,
  lang: Lang,
): string {
  if (serviceTag === "ai-automation" && subKey === "hubspot") {
    return getHubspotBreadcrumbCopy(lang);
  }

  const column = getNavServiceMegaColumn(lang, serviceTag, {
    includeUnpublished: true,
  });
  const link = column?.links.find((item) => item.key === subKey);
  return link?.label ?? subKey;
}

/** Breadcrumb for single service / project detail routes; null elsewhere. */
export function resolveNavBreadcrumb(
  pathname: string,
  lang: Lang,
  links: { label: string; href: string }[],
  deps?: NavBreadcrumbDeps,
): NavBreadcrumbTrail | null {
  const { pathWithoutLocale } = parseLocaleFromPathname(pathname);
  const activeLocale = localeFromPathname(pathname);
  const route = resolveMarketingRoute(pathWithoutLocale, activeLocale);

  if (!route) {
    return null;
  }

  if (route.kind === "service") {
    const service = getServiceByTag(route.serviceTag);
    if (!service) {
      return null;
    }

    return {
      parent: findNavSectionLink(links, activeLocale, "services"),
      current: getNavServiceItems(lang).find((item) => item.tag === service.tag)?.label ?? t(service.title, lang),
    };
  }

  if (route.kind === "service-subpage") {
    const service = getServiceByTag(route.serviceTag);
    if (!service) {
      return null;
    }

    return {
      parent: findNavSectionLink(links, activeLocale, "services"),
      middle: {
        label: getNavServiceItems(lang).find((item) => item.tag === service.tag)?.label ?? t(service.title, lang),
        href: buildServiceHref(activeLocale, route.serviceTag),
      },
      current: serviceSubpageNavCurrentLabel(route.serviceTag, route.subKey, lang),
    };
  }

  if (route.kind === "project") {
    const project = getProjectBySlug(route.projectSlug, lang);
    if (!project) {
      return null;
    }

    return {
      parent: findNavSectionLink(links, activeLocale, "projects"),
      current: project.title,
    };
  }

  if (route.kind === "blog-post") {
    // The article reader owns the title, including editorial and live CMS posts.
    // A persisted layout may still hold the previous article during navigation.
    const navigation = deps?.blogNavigation;
    const current = navigation?.pathname === pathname && navigation.lang === lang && navigation.title
      ? navigation.title
      : blogPostNavCurrentLabel(route.postSlug, lang, deps);

    return {
      parent: blogNavSectionLink(lang, links),
      current,
    };
  }

  if (route.kind === "blog-category") {
    const category = categories.find((item) => item.slug === route.categorySlug);
    return category ? {
      parent: blogNavSectionLink(lang, links),
      current: getBlogCategoryLabel(category, lang),
    } : null;
  }

  return null;
}
